import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { shouldShowValidationError, ValidationFocusDirective } from '@sbd-doug-frontend/shared/lib/public-forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';

interface SearchableSelectOption {
  disabled: boolean;
  _getHostElement(): HTMLElement;
}

interface SearchableSelectKeyManager {
  activeItem: SearchableSelectOption | null;
  setFirstItemActive(): void;
  skipPredicate(predicate: (option: SearchableSelectOption) => boolean): void;
}

@Component({
  selector: 'cipx-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    ValidationFocusDirective,
  ],
})
export class CIPXSearchableSelectComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() label!: string;
  @Input() placeholder = 'Search...';
  @Input() options: any[] = [];
  @Input() valueKey = 'id';
  @Input() labelKey = 'label';
  @Input() multiple = true;
  @Input() appearance: 'fill' | 'outline' = 'fill';
  @Input() sortAlphabetically = true;
  @Input() useOptionAsValue = false;
  @Input() hint = '';
  @Input() hintClass = '';
  @Input() errorMessage = '';
  @Input() compact = true;
  @Input() deferErrorState = false;
  @Input() showValidationErrors = false;

  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild('select') select!: MatSelect;
  @ViewChild('select', { read: ElementRef }) selectElement!: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchControl = new FormControl('');
  filteredOptions: any[] = [];
  hiddenSelectedOptions: any[] = [];
  private _onDestroy = new Subject<void>();
  private closeResetTimer?: ReturnType<typeof setTimeout>;
  private readonly openOnArrowKey = (event: KeyboardEvent) => {
    if (
      !this.select.panelOpen &&
      !this.select.disabled &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'ArrowRight')
    ) {
      // MatSelect normally changes a single-select value when a navigation key
      // is pressed while closed. Handle the event first so the key opens the
      // searchable panel without changing the current selection.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.select.open();
    }
  };

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control: AbstractControl | null, form): boolean => {
      if (!control?.invalid) {
        return false;
      }
      if (this.select?.panelOpen) {
        return false;
      }
      return this.deferErrorState
        ? this.showValidationErrors || !!form?.submitted
        : shouldShowValidationError(control, form);
    },
  };

  ngOnInit() {
    this.searchControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterOptions();
      this.resetActiveOptionAfterFiltering();
    });

    // Handle form control changes to keep filtered options up to date with selections
    this.formGroup
      .get(this.controlName)
      ?.valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterOptions();
      });

    this.filterOptions();
  }

  ngOnChanges(changes: any) {
    if (changes.options) {
      this.filterOptions();
    }
    if (changes.formGroup) {
      this.filterOptions();
    }
  }

  ngAfterViewInit() {
    this.configureKeyboardNavigation();
    this.selectElement.nativeElement.addEventListener('keydown', this.openOnArrowKey, true);

    this.select.openedChange.pipe(takeUntil(this._onDestroy)).subscribe((opened) => {
      if (opened) {
        this.cancelPendingCloseReset();
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
        }, 100);
      } else {
        this.resetSearchAfterPanelCloses();
      }
    });
  }

  ngOnDestroy() {
    this.cancelPendingCloseReset();
    this.selectElement?.nativeElement.removeEventListener('keydown', this.openOnArrowKey, true);
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  filterOptions() {
    if (!this.options) {
      if (this.filteredOptions.length) {
        this.filteredOptions = [];
      }
      if (this.hiddenSelectedOptions.length) {
        this.hiddenSelectedOptions = [];
      }
      return;
    }

    const search = (this.searchControl.value || '').toLowerCase();
    const selectedValues = this.getSelectedValues();

    let filtered;
    if (!search) {
      filtered = [...this.options];
    } else {
      const searchLower = search.toLowerCase();
      filtered = this.options.filter((option) => {
        const label = String(option[this.labelKey] || '').toLowerCase();
        return label.includes(searchLower);
      });
    }

    if (this.sortAlphabetically) {
      filtered.sort((a, b) => {
        const isSelectedA = selectedValues.includes(a[this.valueKey]);
        const isSelectedB = selectedValues.includes(b[this.valueKey]);

        if (isSelectedA && !isSelectedB) return -1;
        if (!isSelectedA && isSelectedB) return 1;

        const labelA = String(a[this.labelKey] || '').toLowerCase();
        const labelB = String(b[this.labelKey] || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
    } else {
      // Even if alphabetical sorting is off, we still want to show selected at the top if requested
      filtered.sort((a, b) => {
        const isSelectedA = selectedValues.includes(a[this.valueKey]);
        const isSelectedB = selectedValues.includes(b[this.valueKey]);
        if (isSelectedA && !isSelectedB) return -1;
        if (!isSelectedA && isSelectedB) return 1;
        return 0;
      });
    }

    // Replacing the array when the visible rows did not change destroys every
    // mat-option. Mat-select then clears the model, which emits valueChanges,
    // which filters again. A new options array on each change-detection pass
    // never settles.
    if (!this.sameOptions(this.filteredOptions, filtered)) {
      this.filteredOptions = filtered;
    }

    // Keep selected options that are not part of the current filtered list rendered
    // (but hidden) so that Angular Material's mat-select does not drop them from the
    // selection when their mat-option is removed from the DOM during filtering.
    const filteredValues = filtered.map((option) => option[this.valueKey]);
    const hidden = this.options.filter(
      (option) => selectedValues.includes(option[this.valueKey]) && !filteredValues.includes(option[this.valueKey])
    );
    if (!this.sameOptions(this.hiddenSelectedOptions, hidden)) {
      this.hiddenSelectedOptions = hidden;
    }
  }

  private sameOptions(current: any[], next: any[]): boolean {
    if (current.length !== next.length) {
      return false;
    }
    return current.every((option, index) => {
      const other = next[index];
      return (
        option === other ||
        (option?.[this.valueKey] === other?.[this.valueKey] && option?.[this.labelKey] === other?.[this.labelKey])
      );
    });
  }

  getSelectedValues(): any[] {
    const control = this.formGroup.get(this.controlName);
    if (!control) return [];
    const value = control.value;
    const values = this.multiple
      ? Array.isArray(value)
        ? value
        : []
      : value !== null && value !== undefined
        ? [value]
        : [];
    return this.useOptionAsValue ? values.map((selectedValue) => selectedValue?.[this.valueKey]) : values;
  }

  compareOptions = (option: any, selectedValue: any): boolean => {
    if (!this.useOptionAsValue) {
      return option === selectedValue;
    }
    return option?.[this.valueKey] === selectedValue?.[this.valueKey];
  };

  clearSearch(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.searchControl.setValue('');
  }

  private resetSearchAfterPanelCloses() {
    this.cancelPendingCloseReset();

    // MatSelect emits openedChange(false) when its exit animation starts. Waiting
    // for Material's detach fallback keeps the visible option list from rebuilding
    // while the panel is fading out.
    this.closeResetTimer = setTimeout(() => {
      this.closeResetTimer = undefined;
      if (!this.select.panelOpen && this.searchControl.value) {
        this.clearSearch();
      }
    }, 200);
  }

  private cancelPendingCloseReset() {
    if (this.closeResetTimer !== undefined) {
      clearTimeout(this.closeResetTimer);
      this.closeResetTimer = undefined;
    }
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.select.close();
      event.stopPropagation();
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'Home') {
      // MatSelect scrolls the active option into view, but it does not account for
      // the search field that precedes the options. Once keyboard navigation reaches
      // the first option, reveal the actual top of the panel as mouse scrolling does.
      setTimeout(() => {
        const firstOption = this.select.options.first;
        if (firstOption?.active) {
          this.select.panel.nativeElement.scrollTop = 0;
        }
      });
    }
  }

  private configureKeyboardNavigation() {
    this.getKeyManager()?.skipPredicate(
      (option) => this.isHiddenSelectedOption(option) || (!this.select.panelOpen && option.disabled)
    );
  }

  private resetActiveOptionAfterFiltering() {
    setTimeout(() => {
      if (!this.select?.panelOpen) {
        return;
      }

      // Filtering rebuilds MatSelect's option list. If the previously active option
      // disappeared, Material can move keyboard navigation to one of the hidden
      // options retained for selection state. Restart from the first visible result.
      this.getKeyManager()?.setFirstItemActive();
    });
  }

  private getKeyManager(): SearchableSelectKeyManager | undefined {
    return (this.select as unknown as { _keyManager?: SearchableSelectKeyManager })._keyManager;
  }

  private isHiddenSelectedOption(option: SearchableSelectOption): boolean {
    return option._getHostElement().classList.contains('hidden-selected-option');
  }

  onOptionSelected() {
    this.selectionChange.emit(this.formGroup.get(this.controlName)?.value);
    if (this.multiple) {
      // Clear search text so that the list is refreshed automatically
      this.clearSearch();
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }
}
